/**
 * The filter bar — ONE bar, for every surface that filters a report.
 *
 * Filters sit ABOVE what they narrow rather than in a configuration column,
 * because they are the question's context — "for these customers, in this
 * period" — while the shelves are its shape. Someone reading a finished report
 * or a dashboard needs the context in view; they rarely need to know how it was
 * assembled.
 *
 * THE HOST OWNS THE FILTERS; THE BAR OWNS THE INTERACTION. Three things are
 * handed in rather than decided here, and they are the three that differ
 * between a report and a dashboard:
 *
 *  - DEFINITIONS (`fields`) — what can be filtered. A report offers its one
 *    model's fields (`filterFieldsOf`); a dashboard offers the union of its
 *    tiles', each noting how many tiles it reaches.
 *  - LOADING (`filters`) — the rules currently on the bar. From a saved spec,
 *    or parsed out of a dashboard's URL.
 *  - SAVING (`onAction`) — what the reader did, as a {@link FilterAction}. The
 *    report builder folds it into its spec reducer; the dashboard applies it
 *    with `applyFilterAction` and writes the result to the URL.
 *
 * Everything else — adding a chip from the menu, the operator, the value
 * editors, taking an unfilled chip back off — is the same on both, because it
 * is the same question asked in the same place.
 *
 * PINNED chips (`pins`) are the one thing a host may add on top. A pinned
 * field is always on the bar, filled in or not, and carries no ✕: the reader
 * may set it, not remove it. A dashboard's author pins the handful of filters
 * that dashboard is about, so a reader lands on "Customer, Employee" rather
 * than on an empty bar or on every dimension the tiles happen to share. Where
 * `pins.onChange` is given — an author, editing — every chip carries a pin
 * toggle.
 *
 * THE PERIOD IS ONE OF THEM. It is the leading chip, and it is a SELECTION
 * rather than a pair of dates: a macro like "previous quarter" is stored as the
 * macro, so a report or dashboard saved over one goes on being asked over it as
 * time moves. First and unremovable — every question is asked over some window.
 *
 * A filter may also spare some rows. Narrowing to one customer shrinks what was
 * logged against them without pretending their team had less capacity — so a
 * ratio falls while its denominator holds. The bar does not claim to explain
 * that: which relations a filter reaches is a property of the SQL, and the
 * browser is not shown the SQL. See `compiler.ts`, decision 4.
 *
 * The chips themselves and the period control come from `@clockwise/ui`; they
 * are presentation, and this file is not — it knows what a dimension is.
 */

import type { DimValue, ReportPeriodPreset } from '@clockwise/semantic-core';
import {
  type DateRangeValue,
  DEFAULT_OP,
  type Dimension,
  describeFilter,
  describeValue,
  type FilterNode,
  type FilterOp,
  type FilterRule,
  type FilterValueType,
  filterOptions,
  filterValueType,
  findDimension,
  isFilterGroup,
  MAX_PERIOD_DATE,
  MEMBER_OPS,
  MIN_PERIOD_DATE,
  opArity,
  operatorsFor,
  opLabel,
  periodProblem,
  type ReportModel,
  staticOptions,
} from '@clockwise/semantic-core';

import {
  DATE_RANGE_PRESET_LABELS,
  DATE_RANGE_PRESETS,
  FilterBarShell,
  FilterChip,
  FilterChipNote,
  FilterChipTrigger,
  FilterOperatorSwitch,
  PeriodChip,
  Picker,
} from '@clockwise/ui';
import type { ReactNode, RefObject } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDismiss } from '../builder/use-dismiss';
import type { FilterAction } from './filter-actions';
import type { FilterField } from './filter-fields';

/** What a host needs to render its own control for one filter. */
export interface FilterControlContext {
  filter: Dimension;
  /** The model the filter belongs to — a member list is only meaningful under
   *  the model whose rows produced it, so the host has to name both. */
  model: ReportModel;
  /**
   * The name to print on the chip. The FIELD's label, which a host may have
   * overridden — so read this rather than `filter.label`.
   */
  label: string;
  /** Disambiguator for a label two chips share. See `FilterField.hint`. */
  hint?: string;
  values: string[];
  onChange: (values: string[]) => void;
  /**
   * The rule's operator in the words its column reads it in — "is", "is not".
   * Print it beside the dimension's name: the chip carries no operator control
   * of its own, so this is the only place on the bar an exclusion shows.
   * EMPTY on a pinned chip that holds nothing and is on the default `is`, so
   * it reads "Customer" rather than half a sentence while it waits.
   */
  opLabel: string;
  /**
   * The is / is not switch, or null when this column has nothing to switch.
   * Draw it with `FilterOperatorSwitch` at the top of the panel, above the
   * search box (`EntityPicker`'s `header`). Switching keeps the picks.
   */
  operatorSwitch: OperatorSwitch | null;
  /** True on the render right after the filter was added. */
  openOnMount: boolean;
  /**
   * The CHIP. Anchor the control's panel to it.
   *
   * A hosted control is the value half of a chip whose other half is the
   * dimension's name, which the HOST draws — `FilterChipLabel` from
   * `@clockwise/ui` is the piece for it. A panel anchored to the control alone
   * opened indented by the width of that name — reading as a panel belonging to
   * nothing on the bar, and the further right the chip sat the more it looked
   * like it had come from the middle of the page. The chip is the thing the
   * reader clicked, so the chip is what the panel lines up with.
   */
  anchorRef: RefObject<HTMLDivElement | null>;
  /**
   * Call when the control's panel closes.
   *
   * A filter is added in order to be given a value; one dismissed without ever
   * getting one is a chip nobody asked for, and it comes back off the bar. The
   * bar decides that — it is the only thing that knows whether the filter still
   * holds values — so the control only has to report the dismissal.
   */
  onClose: () => void;
  /**
   * The period, `YYYY-MM-DD`.
   *
   * Passed because a host that fetches members needs it twice over: the values
   * worth offering are the ones the period actually contains, and row
   * visibility can itself depend on the window — who led which customer is a
   * question about a date range, not about today. A control fetching without
   * it would answer a different question from the report it sits above.
   */
  from: string;
  to: string;
}

/** The member operators a chip may flip between, and what to call on a flip. */
export interface OperatorSwitch {
  value: FilterOp;
  options: readonly { value: FilterOp; label: string }[];
  onChange: (op: FilterOp) => void;
}

/**
 * Split a member chip's operators into the SWITCH and the rest.
 *
 * The switch is `in` / `not_in` — the two questions a list of members answers —
 * and is drawn in the panel. Anything else the column offers (a date's "is
 * within", "is before") has no place in a members panel and keeps the menu on
 * the chip. A lookup offers nothing else, so its chip has no menu at all.
 */
function splitOperators(
  op: FilterOp,
  operators: readonly FilterOp[],
  type: FilterValueType,
  onOp: (op: FilterOp) => void,
): { operatorSwitch: OperatorSwitch | null; others: readonly FilterOp[] } {
  const members = (MEMBER_OPS as readonly FilterOp[]).filter((m) => operators.includes(m));
  const others = operators.filter((o) => !members.includes(o));
  return {
    operatorSwitch:
      members.length > 1 && members.includes(op)
        ? {
            value: op,
            options: members.map((m) => ({ value: m, label: opLabel(m, type) })),
            onChange: onOp,
          }
        : null,
    // The menu only earns its place when it offers somewhere the switch does
    // not go; with the member operators in it too, it is the whole list.
    others: others.length ? operators : [],
  };
}

/**
 * The operator as a member chip prints it — nothing on a PINNED chip that is
 * empty and still on the default `is`. `RuleChip` applies the same rule to its
 * own default operator.
 *
 * A chip just added reads "Stage is" on purpose: it is about to be filled in,
 * and the operator is part of its name. A pinned chip is different — it sits
 * on the bar empty until somebody fills it in, which may be never, and "Stage
 * is" with nothing after it is half a sentence left standing. An exclusion
 * still says so: "Stage is not" is a choice, even before anyone is picked.
 */
function chipOpLabel(
  op: FilterOp,
  type: FilterValueType,
  values: readonly string[],
  pinned: boolean,
): string {
  return pinned && values.length === 0 && op === 'in' ? '' : opLabel(op, type);
}

/** The leading chip. Every question is asked over some window. */
export interface FilterBarPeriod {
  /** The selection — a macro, or two dates. What the chip shows and edits. */
  value: DateRangeValue;
  /**
   * The window actually being queried, `YYYY-MM-DD`.
   *
   * Passed rather than re-resolved from `value`: a macro on a spec is resolved
   * when the spec arrives and then held, so a builder left open past midnight
   * on the last of the month would otherwise have the chip name the new month
   * over a report still covering the old one. It is also what member lookups
   * are asked over.
   */
  window: { from: string; to: string };
  onChange: (range: DateRangeValue) => void;
  /**
   * The periods the chip offers. Every report period when omitted; a host
   * narrows it from the model (`modelPeriods`) or, over several models, to the
   * ones they all offer (`sharedPeriods`).
   */
  presets?: readonly ReportPeriodPreset[];
  /**
   * Why the host will not take this selection, in a sentence, or null.
   *
   * Checked on top of the span ceiling every report has. The chip shows the
   * sentence and keeps its panel open, so a host whose own reducer refuses a
   * selection should say so here rather than let the chip close over a change
   * that was never made.
   */
  problem?: (range: DateRangeValue) => string | null;
}

/** Which fields are pinned to the bar, and — for an author — how to change that. */
export interface FilterPins {
  /** Field ids, in the order their chips lead the bar. */
  pinned: readonly string[];
  /** Present when this viewer may pin and unpin. Every chip then carries a toggle. */
  onChange?: (next: string[]) => void;
  /**
   * How many may be pinned. At the limit an unpinned chip offers no pin — a
   * toggle that the host would then refuse to save is worse than none.
   */
  limit?: number;
}

export interface FilterBarProps {
  /** DEFINITIONS: what can be filtered. See `FilterField`. */
  fields: readonly FilterField[];
  /**
   * LOADING: the filters on the bar now.
   *
   * A rule naming a field that is not in `fields` is not drawn — on a
   * dashboard that is a filter in a shared link for a dimension no tile has
   * any more, and a chip that narrows nothing is worse than none.
   */
  filters: readonly FilterNode[];
  /** SAVING: what the reader did. Apply it with `applyFilterAction`, or your own reducer. */
  onAction: (action: FilterAction) => void;
  period: FilterBarPeriod;
  /**
   * Option lists per field id, supplied by the host.
   *
   * Overrides whatever the dimension declares. A filter with neither falls back
   * to the model's own authored list, and failing that to free text.
   */
  options?: Record<string, DimValue[]>;
  /**
   * Supply the host's own value control for a filter — return null to keep the
   * built-in one.
   *
   * This is the seam that lets Clockwise put its real customer and employee
   * pickers on the bar instead of a second, near-identical autocomplete living
   * in here. The library stays free of the app; the app stops forking widgets.
   */
  renderControl?: (context: FilterControlContext) => ReactNode;
  pins?: FilterPins;
  /**
   * Whether the reader may add filters of their own. Off while a host is still
   * working out what `fields` is — a menu offered before then would change
   * under the pointer.
   */
  canAdd?: boolean;
  /** The bar's leading label. Empty for none. */
  label?: string;
  /** Announced while the host is still working out the bar. */
  busy?: boolean;
  /**
   * Draw the leading period chip? Default yes.
   *
   * PASS FALSE WHEN THE PERIOD IS DECIDED ELSEWHERE — a dashboard tile's own
   * filters, where the window is the dashboard's and a chip here would offer a
   * choice that changes nothing. `period.window` is still what member lookups
   * are asked over.
   */
  showPeriod?: boolean;
  /** The bar's frame. Defaults to the builder's band (`rk-filterbar`). */
  className?: string;
  /** Host content after the chips — a Clear button, a status line. */
  children?: ReactNode;
}

const NO_PINS: readonly string[] = [];
const NO_OPTIONS: Record<string, DimValue[]> = {};

/** What a chip needs from the bar besides its own value. */
interface ChipChrome {
  /** Absent on a pinned chip: the reader may fill it in, not take it off. */
  onRemove?: () => void;
  removeLabel: string;
  pinned: boolean;
  onPinToggle?: () => void;
  pinLabel: string;
  note?: string;
}

export function FilterBar({
  fields,
  filters,
  onAction,
  period,
  options = NO_OPTIONS,
  renderControl,
  pins,
  canAdd = true,
  label = 'Filters',
  busy = false,
  showPeriod = true,
  className,
  children,
}: FilterBarProps) {
  const { from, to } = period.window;
  const byId = useMemo(() => new Map(fields.map((f) => [f.id, f])), [fields]);
  const pinned = pins?.pinned ?? NO_PINS;

  /**
   * Does the MEMBER path own this field? Two conditions, and both matter.
   *
   * There has to be a list to pick from — `options: 'none'` means one value
   * per row, which is a substring question — and the rule has to be the
   * operator that picks from it. `Customer is none of …` and `QB ID contains …`
   * both need an operator on the chip, so they go to {@link RuleChip}, which
   * still borrows the host's picker when the values are member keys.
   */
  const dimensionOf = useCallback(
    (field: string): Dimension | undefined => {
      const def = byId.get(field);
      return def ? findDimension(def.model, field) : undefined;
    },
    [byId],
  );
  const memberBacked = useCallback(
    (field: string) => {
      const dimension = dimensionOf(field);
      return dimension !== undefined && filterOptions(dimension) !== 'none';
    },
    [dimensionOf],
  );
  const isMemberChip = useCallback(
    (rule: FilterRule) => (rule.op === 'in' || rule.op === 'not_in') && memberBacked(rule.field),
    [memberBacked],
  );

  /**
   * A PLACEHOLDER rule for each pinned field that has none yet.
   *
   * A pinned chip is on the bar whether or not it holds anything, but an empty
   * filter must not travel with what the host saves — it constrains nothing.
   * So the bar draws one it does not own, and the first value the reader gives
   * it is dispatched as an `addFilter` carrying the SAME id: the chip keeps its
   * React identity, and its open panel, as it becomes a real rule.
   */
  const placeholders = useMemo(() => {
    const taken = new Set(filters.map((node) => node.id));
    const ruled = new Set(filters.flatMap((node) => (isFilterGroup(node) ? [] : [node.field])));
    return pinned.flatMap((field): FilterRule[] => {
      const def = byId.get(field);
      if (!def || ruled.has(field)) return [];
      const type = filterValueType(def.model, field);
      if (!type) return [];
      const id = taken.has(field) ? `${field}-pinned` : field;
      return [{ id, field, op: DEFAULT_OP[type], paused: false }];
    });
  }, [filters, pinned, byId]);
  const placeholderIds = useMemo(() => new Set(placeholders.map((r) => r.id)), [placeholders]);

  /**
   * ORDER: pinned chips lead, in pin order; then everything else in the order
   * it was added.
   *
   * Position is how somebody keeps track of which chip is which while they
   * fill three of them in, so it must not encode a state that changes under
   * the pointer — a chip does not jump when it is given a value. Pinning is an
   * author's arrangement, not a reader's state, so it may.
   *
   * The pinned chip for a field is the FIRST top-level rule on it. A second
   * rule on the same column (`contains audit` AND `does not contain internal`)
   * is an ordinary chip of the reader's.
   */
  const { ordered, pinnedIds } = useMemo(() => {
    const all: FilterNode[] = [...filters, ...placeholders];
    const lead: FilterNode[] = [];
    const ids = new Set<string>();
    for (const field of pinned) {
      const rule = all.find((node) => !isFilterGroup(node) && node.field === field);
      if (rule && !ids.has(rule.id)) {
        lead.push(rule);
        ids.add(rule.id);
      }
    }
    return { ordered: [...lead, ...filters.filter((node) => !ids.has(node.id))], pinnedIds: ids };
  }, [filters, placeholders, pinned]);

  // The filter added most recently, so its values open straight away. Adding
  // a filter is never the goal — choosing what to filter TO is — and making
  // that a second click puts a step between the intent and the result.
  const [justAdded, setJustAdded] = useState<string | null>(null);

  // One member chip per COLUMN from the menu: it is that column's picker, so a
  // second would be two controls over one list. A rule chip is different —
  // `contains audit` AND `does not contain internal` is two rules on one
  // column, which is what per-chip ids exist for.
  // A PINNED field is on the bar whatever kind of chip it is, so it is never
  // offered again: a second rule added from the menu would take over as the
  // pinned chip, empty and with no ✕.
  const onBar = useMemo(
    () =>
      new Set([
        ...pinned,
        ...ordered.flatMap((node) =>
          !isFilterGroup(node) && isMemberChip(node) ? [node.field] : [],
        ),
      ]),
    [ordered, isMemberChip, pinned],
  );
  const available = fields.filter((f) => !onBar.has(f.id));

  /**
   * What each filter holds RIGHT NOW — including a value committed earlier in
   * this same event.
   *
   * `filters` is a prop, so it does not move until the host's next render.
   * That is a whole event too late for {@link closed}: a control that commits
   * a value and closes its panel on one click — which `Picker` does for a
   * single-select pick — would be judged against the state from before its own
   * pick, and the chip would be dropped and the pick cleared behind it. So
   * every write this bar makes is mirrored here as it is dispatched, and the
   * prop overwrites the mirror on the next render, which is when the host has
   * caught up.
   */
  const latest = useRef<Record<string, string[]>>({});
  latest.current = Object.fromEntries(
    ordered.flatMap((node) =>
      isFilterGroup(node)
        ? []
        : [[node.id, node.value?.kind === 'list' ? node.value.values : []] as const],
    ),
  );

  /**
   * Change a rule — or, for a pinned chip's placeholder, CREATE it.
   *
   * A placeholder becomes a rule only once it says something: a value, or an
   * operator somebody chose. Clearing one that is still a placeholder is
   * nothing to save.
   */
  const patch = (rule: FilterRule, change: Partial<Omit<FilterRule, 'id'>>) => {
    if (placeholderIds.has(rule.id)) {
      if (change.value === undefined && change.op === undefined) return;
      onAction({
        type: 'addFilter',
        dimensionId: rule.field,
        id: rule.id,
        op: change.op ?? rule.op,
        value: change.value ?? rule.value,
      });
      return;
    }
    onAction({ type: 'setFilterRule', filterId: rule.id, patch: change });
  };

  const remove = (ruleId: string) => {
    setJustAdded(null);
    // A placeholder is not the host's to remove; it simply stays empty.
    if (placeholderIds.has(ruleId)) return;
    onAction({ type: 'removeFilter', filterId: ruleId });
  };

  /**
   * A member chip's values, kept under the RULE'S OWN operator.
   *
   * Emptied, an ordinary chip leaves the bar. A PINNED one keeps its rule with
   * no value instead: removing it would swap it for its placeholder, a
   * different chip, and the panel the reader is clearing it in would close
   * under their hand. It is taken off when that panel closes — see
   * {@link closed} — which leaves the placeholder, looking exactly the same.
   */
  const setValues = (rule: FilterRule, values: string[]) => {
    latest.current = { ...latest.current, [rule.id]: values };
    if (values.length > 0) patch(rule, { value: { kind: 'list', values } });
    else if (pinnedIds.has(rule.id)) patch(rule, { value: undefined });
    else remove(rule.id);
  };

  /**
   * Every chip is a RULE from the moment it is added — so there is always a
   * rule to patch, and choosing an operator on a chip that has no value yet
   * does something.
   */
  const add = (field: string) => {
    setJustAdded(field);
    onAction({ type: 'addFilter', dimensionId: field });
  };

  /**
   * A chip's panel was dismissed. If nothing was chosen, the chip goes.
   *
   * A filter is added in order to be given a value — the panel opens on the
   * same click for exactly that reason — so one closed without a value is a
   * step somebody started and abandoned, and leaving it behind puts a control
   * on the bar that narrows nothing and has to be tidied away by hand. Read
   * from what the filter holds rather than remembered, so a chip that WAS
   * filled and then emptied is treated the same as one that never was.
   *
   * A pinned chip goes too, and is replaced by its placeholder — which is
   * what an empty pinned chip IS, so nothing on the bar moves.
   */
  const closed = (ruleId: string) => {
    if ((latest.current[ruleId] ?? []).length === 0) remove(ruleId);
  };

  const atLimit = pins?.limit !== undefined && pinned.length >= pins.limit;
  const togglePin = pins?.onChange
    ? (field: string) => {
        const next = pinned.includes(field)
          ? pinned.filter((p) => p !== field)
          : [...pinned, field];
        pins.onChange?.(next);
      }
    : undefined;

  const chromeFor = (rule: FilterRule, def: FilterField): ChipChrome => {
    const isPinned = pinnedIds.has(rule.id);
    return {
      onRemove: isPinned ? undefined : () => remove(rule.id),
      removeLabel: `Remove the ${def.label} filter`,
      pinned: isPinned,
      onPinToggle: togglePin && (isPinned || !atLimit) ? () => togglePin(rule.field) : undefined,
      pinLabel: isPinned ? `Unpin the ${def.label} filter` : `Pin the ${def.label} filter`,
      note: def.note,
    };
  };

  return (
    // `rk-scope` ALWAYS: the chips' own panels are drawn with the library's
    // tokens, and a bar mounted outside a builder — over a dashboard — would
    // otherwise resolve every one of them to nothing and paint a see-through
    // panel over the tiles. See the note on `.rk-scope` in `styles.css`.
    <FilterBarShell
      label={label || undefined}
      busy={busy}
      className={`rk-scope ${className ?? 'rk-filterbar'}`}
    >
      {/* THE PERIOD IS A FILTER, and it sits here with the rest of them rather
          than in a band of its own above the bar. It narrows the report the
          same way a customer or an activity does — "for these customers, in
          this month" is one sentence. FIRST and unremovable: every question is
          asked over some window, so there is no state in which it has no
          period, only ones where the period is wider. */}
      {showPeriod && (
        <PeriodChip
          value={period.value}
          onChange={period.onChange}
          // `DATE_RANGE_PRESETS` unless the host narrows it, which
          // deliberately excludes `all_time` (it resolves to no window, and a
          // spec with no window will not compile) and `current_week` (no
          // control offers it).
          presets={period.presets ?? DATE_RANGE_PRESETS}
          window={period.window}
          // The calendar reaches exactly as far as a report may be asked over,
          // with a jumping caption — this control is the only way to name a
          // date, and stepping an arrow to a month four years back is not a way.
          earliest={MIN_PERIOD_DATE}
          latest={MAX_PERIOD_DATE}
          // A window no report may be asked over is refused HERE, with a reason,
          // rather than after the query has come back empty or been refused.
          problem={(resolved, range) =>
            periodProblem(resolved.from, resolved.to) ?? period.problem?.(range) ?? null
          }
        />
      )}

      {ordered.map((node) => {
        if (isFilterGroup(node)) {
          // A group is still a sentence nobody can edit here — but it is
          // SHOWN, because a filter the reader cannot see is the one thing
          // this design refuses. Removing it works.
          const first = node.children[0];
          const def = first ? byId.get(first.field) : undefined;
          if (!def) return null;
          const text = describeFilter(def.model, node);
          return (
            <FilterChip
              key={node.id}
              filterId={node.id}
              active
              onRemove={() => onAction({ type: 'removeFilter', filterId: node.id })}
              removeLabel={`Remove the ${text} filter`}
            >
              <FilterChipTrigger name={text} expanded={false} onClick={() => {}} />
            </FilterChip>
          );
        }

        const def = byId.get(node.field);
        if (!def) return null;
        const type = filterValueType(def.model, node.field) ?? 'text';
        const chrome = chromeFor(node, def);
        const dimension = dimensionOf(node.field);
        const fieldOptions =
          options[node.field] ?? (dimension ? staticOptions(dimension) : undefined) ?? [];

        if (dimension && isMemberChip(node)) {
          return (
            <ChipSlot
              key={node.id}
              ruleId={node.id}
              filter={dimension}
              field={def}
              values={node.value?.kind === 'list' ? node.value.values : []}
              // The host's list wins; failing that, the vocabulary the MODEL
              // authored. A dimension whose members are a closed set of words
              // — a status, a boolean — needs no round trip and no host
              // control, so the bar can offer it on its own.
              options={fieldOptions}
              op={node.op}
              operators={operatorsFor(def.model, node.field)}
              onOp={(op) => patch(node, { op })}
              openOnMount={justAdded === node.field && node.value === undefined}
              from={from}
              to={to}
              renderControl={renderControl}
              chrome={chrome}
              onChange={(next) => setValues(node, next)}
              onClose={() => closed(node.id)}
            />
          );
        }

        return (
          <RuleChip
            key={node.id}
            rule={node}
            field={def}
            operators={operatorsFor(def.model, node.field)}
            type={type}
            options={fieldOptions}
            // The chip just added is the one with NO VALUE yet: `addFilter`
            // generates its id, so the bar remembers the field — and a field
            // may carry several rules, which had every chip on that column
            // opening its editor at once.
            openOnMount={justAdded === node.field && node.value === undefined}
            chrome={chrome}
            onPatch={(change) => patch(node, change)}
          />
        );
      })}

      {children}

      {canAdd && (
        <Picker
          trigger="add"
          placeholder="Filter"
          title="Add a filter"
          searchPlaceholder="Search filters…"
          emptyText="Every filter is already on the bar."
          options={available.map((f) => ({
            id: f.id,
            label: f.label,
            description: [f.note, f.hint ?? f.description].filter(Boolean).join(' · ') || undefined,
          }))}
          // Stateless: picking ADDS a chip rather than setting a value here, so
          // the control never holds a selection of its own to show or clear.
          value={null}
          clearable={false}
          onChange={(field) => {
            if (field) add(field);
          }}
          popoverWidth="auto"
        />
      )}
    </FilterBarShell>
  );
}

/**
 * One chip on the bar — whichever kind it turns out to be.
 *
 * It exists to OWN THE CHIP ELEMENT. Both kinds need a ref to it: the built-in
 * chip to know a press landed outside itself, and a hosted control to anchor
 * its panel to the whole chip rather than to the half of it the control draws.
 * The bar renders these in a `.map`, where a ref per chip cannot be made — so
 * the ref lives one level down, in the thing it points at.
 *
 * Which kind is the host's answer: `renderControl` returning null means "not
 * mine", and a dimension the model already stated a vocabulary for must not
 * lose it to an empty hosted slot.
 */
function ChipSlot({
  ruleId,
  filter,
  field,
  values,
  options,
  op,
  operators,
  onOp,
  openOnMount,
  from,
  to,
  renderControl,
  chrome,
  onChange,
  onClose,
}: {
  /** The RULE this chip is: two rules on one column are two chips. */
  ruleId: string;
  filter: Dimension;
  field: FilterField;
  values: string[];
  options: readonly DimValue[];
  /** The operator the rule carries — `is any of` until somebody changes it. */
  op: FilterOp;
  operators: readonly FilterOp[];
  onOp: (op: FilterOp) => void;
  openOnMount: boolean;
  from: string;
  to: string;
  renderControl?: (context: FilterControlContext) => ReactNode;
  chrome: ChipChrome;
  onChange: (values: string[]) => void;
  onClose: () => void;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const type = filterValueType(field.model, filter.id) ?? 'lookup';
  const { operatorSwitch, others } = splitOperators(op, operators, type, onOp);
  const hosted = renderControl?.({
    filter,
    model: field.model,
    label: field.label,
    hint: field.hint,
    values,
    onChange,
    opLabel: chipOpLabel(op, type, values, chrome.pinned),
    operatorSwitch,
    openOnMount,
    anchorRef,
    onClose,
    from,
    to,
  });

  if (hosted) {
    // The chip's INSIDE is entirely the host's — the dimension's name included,
    // which is why `FilterChipLabel` is exported from `@clockwise/ui` rather
    // than drawn here. A control that brings its own trigger has to sit beside
    // that name, and only the host knows how wide its trigger wants to be.
    return (
      <Chip ref={anchorRef} ruleId={ruleId} active={values.length > 0} chrome={chrome}>
        {hosted}
        {others.length > 1 && (
          <OperatorSegment op={op} operators={others} type={type} onChange={onOp} />
        )}
      </Chip>
    );
  }

  return (
    <OptionChip
      containerRef={anchorRef}
      ruleId={ruleId}
      filter={filter}
      label={field.label}
      hint={field.hint}
      values={values}
      options={options}
      op={op}
      type={type}
      operatorSwitch={operatorSwitch}
      others={others}
      onOp={onOp}
      openOnMount={openOnMount}
      chrome={chrome}
      onChange={onChange}
      onClose={onClose}
    />
  );
}

/** The shared chip, with the bar's chrome — its pin, its ✕, its note — applied. */
function Chip({
  ref,
  ruleId,
  active,
  chrome,
  children,
}: {
  ref?: RefObject<HTMLDivElement | null>;
  ruleId: string;
  active: boolean;
  chrome: ChipChrome;
  children: ReactNode;
}) {
  return (
    <FilterChip
      ref={ref}
      filterId={ruleId}
      active={active}
      onRemove={chrome.onRemove}
      removeLabel={chrome.removeLabel}
      pinned={chrome.pinned}
      onPinToggle={chrome.onPinToggle}
      pinLabel={chrome.pinLabel}
    >
      {children}
      {chrome.note && <FilterChipNote>{chrome.note}</FilterChipNote>}
    </FilterChip>
  );
}

/**
 * The operator, as its own control on the chip.
 *
 * Lifted out of {@link RuleChip} so the MEMBER chip can carry one too. Without
 * it, every column with members — Customer, Employee, Activity, Department,
 * and the rest of the lookups — was label-and-values only: the picker writes
 * `is any of` and there was nowhere to ask for `is none of` or `name
 * contains`, so "column · operator · value" held for numbers and free text and
 * not for the columns most reports filter on.
 */
function OperatorSegment({
  op,
  operators,
  type,
  onChange,
}: {
  op: FilterOp;
  operators: readonly FilterOp[];
  type: FilterValueType;
  onChange: (op: FilterOp) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, ref, close);

  return (
    <div className="rk-op-seg" ref={ref}>
      <button
        type="button"
        className="rk-op-trigger"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {opLabel(op, type)}
      </button>
      {open && (
        <div className="rk-popover rk-popover-ops" role="dialog" aria-label="Operator">
          <div className="rk-ops">
            {operators.map((candidate) => (
              <button
                type="button"
                key={candidate}
                className={candidate === op ? 'rk-op rk-op-on' : 'rk-op'}
                onClick={() => {
                  onChange(candidate);
                  close();
                }}
              >
                {opLabel(candidate, type)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The members half of `is any of` — the only operator that takes a list.
 *
 * Every other string operator takes ONE value, because "starts with phone,
 * holiday" was two questions in a control that read as one: nothing on the
 * chip said the terms were ORed, so the reader could not tell what they were
 * looking at. Picking from a list is different — the set is shown, and
 * choosing three customers is plainly three choices.
 */
function MemberEditor({
  values,
  label,
  options,
  onChange,
}: {
  values: readonly string[];
  label: string;
  options: readonly DimValue[];
  onChange: (values: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const labelOf = (key: string) => options.find((o) => o.key === key)?.label ?? key;
  const matches = needle
    ? options.filter((o) => (o.label ?? o.key ?? '').toLowerCase().includes(needle))
    : options;

  return (
    <>
      <input
        className="rk-search"
        type="search"
        // biome-ignore lint/a11y/noAutofocus: the popover opened on an explicit click
        autoFocus
        placeholder={`Search ${label.toLowerCase()}…`}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <div className="rk-options">
        {matches.length === 0 && <p className="rk-options-empty">Nothing matches “{query}”.</p>}
        {matches.map((option) => {
          const key = option.key ?? '';
          return (
            <label className="rk-check" key={key}>
              <input
                type="checkbox"
                checked={values.includes(key)}
                onChange={() =>
                  onChange(
                    values.includes(key) ? values.filter((v) => v !== key) : [...values, key],
                  )
                }
              />
              <span>{labelOf(key)}</span>
            </label>
          );
        })}
      </div>
    </>
  );
}

/**
 * A chip for a RULE: the operator, and whatever value that operator takes.
 *
 * The operator is part of the question, so it is part of the chip — `QB ID
 * contains 1486`, `Hours ≥ 8`, `Approved on is within Previous month` all read
 * left to right as the sentence they compile to. What each operator's value
 * looks like is read from its ARITY, so a new operator needs no new chip.
 *
 * The one thing it does not own is `is any of` over a column with members:
 * that stays with the picker beside it, which has the search, the selected-
 * first ordering and the truncation footer that a thousand customers need.
 */
function RuleChip({
  rule,
  field,
  operators,
  type,
  options,
  openOnMount,
  chrome,
  onPatch,
}: {
  rule: FilterRule;
  field: FilterField;
  /** What this FIELD offers — read from the model, never guessed from the value. */
  operators: readonly FilterOp[];
  type: FilterValueType;
  options: readonly DimValue[];
  openOnMount: boolean;
  chrome: ChipChrome;
  onPatch: (patch: Partial<Omit<FilterRule, 'id'>>) => void;
}) {
  const { label } = field;
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(openOnMount);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, containerRef, close);

  /**
   * Only the operators this control can FILL.
   *
   * A menu entry with no editor behind it is a dead end: picking it leaves the
   * rule without a value, and the report comes back "needs a value" with no
   * way to give it one. A number column — as opposed to a measure — also
   * offers `is any of`, which wants a token list this chip does not have, so
   * it is left out rather than shown broken. It comes back with the editors.
   */
  const fillable = operators;
  const arity = opArity(rule.op, type);
  const value = rule.value;
  const one = value?.kind === 'number' ? value.value : undefined;
  const range = value?.kind === 'numberRange' ? value : undefined;

  /** A blank field means "no bound on this end", which is not the same as 0. */
  const num = (text: string): number | null => {
    const trimmed = text.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // ONE renderer for what a value reads as, shared with the export's About
  // sheet and any refusal — see `describeValue`. Through the option list, so a
  // member key is printed as its LABEL: "is none of Acme", never "is none of
  // 8f3a-4c21-…", and "Billable" rather than "true".
  const summary =
    arity === 'none'
      ? undefined
      : describeValue(rule.value, (key) => options.find((o) => o.key === key)?.label ?? key) ||
        undefined;

  return (
    <Chip
      ref={containerRef}
      ruleId={rule.id}
      active={rule.value !== undefined || arity === 'none'}
      chrome={chrome}
    >
      <FilterChipTrigger
        // An EMPTY pinned chip on its default operator reads as the field alone
        // — see `chipOpLabel`; it waits there, maybe for good.
        name={
          chrome.pinned &&
          rule.value === undefined &&
          arity !== 'none' &&
          rule.op === DEFAULT_OP[type]
            ? label
            : `${label} ${opLabel(rule.op, type)}`
        }
        value={summary}
        expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      />

      {open && (
        <div className="rk-popover rk-popover-values" role="dialog" aria-label={label}>
          <div className="rk-ops">
            {fillable.map((op) => (
              <button
                type="button"
                key={op}
                className={op === rule.op ? 'rk-op rk-op-on' : 'rk-op'}
                // The VALUE is not cleared here: the reducer keeps it when the
                // new operator reads the same shape, which is what makes
                // "≥ 40" → "between 40 – …" the cheap edit it looks like.
                onClick={() => onPatch({ op })}
              >
                {opLabel(op, type)}
              </button>
            ))}
          </div>

          {arity === 'list' && (
            <MemberEditor
              values={rule.value?.kind === 'list' ? rule.value.values : []}
              label={label}
              options={options}
              onChange={(values) =>
                onPatch({ value: values.length ? { kind: 'list', values } : undefined })
              }
            />
          )}

          {arity === 'text' && (
            <>
              <input
                className="rk-search"
                type="text"
                // biome-ignore lint/a11y/noAutofocus: the popover opened on an explicit click
                autoFocus
                placeholder={`Type a ${label.toLowerCase()}…`}
                defaultValue={rule.value?.kind === 'text' ? rule.value.value : ''}
                onChange={(event) => {
                  const text = event.target.value.trim();
                  onPatch({ value: text ? { kind: 'text', value: text } : undefined });
                }}
              />
              <p className="rk-options-empty">
                One value. Two questions are two filters — "either" is a group.
              </p>
            </>
          )}

          {arity === 'macro' && (
            <div className="rk-presets">
              {DATE_RANGE_PRESETS.filter((preset) => preset !== 'custom').map((preset) => (
                <button
                  type="button"
                  key={preset}
                  className={
                    rule.value?.kind === 'macro' && rule.value.preset === preset
                      ? 'rk-op rk-op-on'
                      : 'rk-op'
                  }
                  onClick={() => onPatch({ value: { kind: 'macro', preset } })}
                >
                  {DATE_RANGE_PRESET_LABELS[preset]}
                </button>
              ))}
            </div>
          )}

          {arity === 'date' && (
            <input
              className="rk-search"
              type="date"
              // biome-ignore lint/a11y/noAutofocus: the popover opened on an explicit click
              autoFocus
              defaultValue={rule.value?.kind === 'date' ? rule.value.value : ''}
              onChange={(event) =>
                onPatch({
                  value: event.target.value
                    ? { kind: 'date', value: event.target.value }
                    : undefined,
                })
              }
            />
          )}

          {arity === 'dateRange' && (
            <div className="rk-range">
              <input
                className="rk-search"
                type="date"
                // biome-ignore lint/a11y/noAutofocus: the popover opened on an explicit click
                autoFocus
                defaultValue={rule.value?.kind === 'dateRange' ? (rule.value.from ?? '') : ''}
                onChange={(event) =>
                  onPatch({
                    value: {
                      kind: 'dateRange',
                      from: event.target.value || null,
                      to: rule.value?.kind === 'dateRange' ? rule.value.to : null,
                    },
                  })
                }
              />
              <span className="rk-range-sep">and</span>
              <input
                className="rk-search"
                type="date"
                defaultValue={rule.value?.kind === 'dateRange' ? (rule.value.to ?? '') : ''}
                onChange={(event) =>
                  onPatch({
                    value: {
                      kind: 'dateRange',
                      from: rule.value?.kind === 'dateRange' ? rule.value.from : null,
                      to: event.target.value || null,
                    },
                  })
                }
              />
            </div>
          )}

          {arity === 'boolean' && (
            <div className="rk-ops">
              {[
                { value: true, label: 'Yes' },
                { value: false, label: 'No' },
              ].map((choice) => (
                <button
                  type="button"
                  key={choice.label}
                  className={
                    rule.value?.kind === 'boolean' && rule.value.value === choice.value
                      ? 'rk-op rk-op-on'
                      : 'rk-op'
                  }
                  onClick={() => onPatch({ value: { kind: 'boolean', value: choice.value } })}
                >
                  {choice.label}
                </button>
              ))}
            </div>
          )}

          {arity === 'number' && (
            <input
              className="rk-search"
              type="text"
              inputMode="decimal"
              // biome-ignore lint/a11y/noAutofocus: the popover opened on an explicit click
              autoFocus
              placeholder="Value"
              defaultValue={one ?? ''}
              onChange={(event) => {
                const parsed = num(event.target.value);
                onPatch(
                  parsed === null
                    ? { value: undefined }
                    : { value: { kind: 'number', value: parsed } },
                );
              }}
            />
          )}

          {arity === 'numberRange' && (
            <div className="rk-range">
              <input
                className="rk-search"
                type="text"
                inputMode="decimal"
                // biome-ignore lint/a11y/noAutofocus: the popover opened on an explicit click
                autoFocus
                placeholder="Min"
                defaultValue={range?.min ?? ''}
                onChange={(event) =>
                  onPatch({
                    value: {
                      kind: 'numberRange',
                      min: num(event.target.value),
                      max: range?.max ?? null,
                    },
                  })
                }
              />
              <span className="rk-range-sep">and</span>
              <input
                className="rk-search"
                type="text"
                inputMode="decimal"
                placeholder="Max"
                defaultValue={range?.max ?? ''}
                onChange={(event) =>
                  onPatch({
                    value: {
                      kind: 'numberRange',
                      min: range?.min ?? null,
                      max: num(event.target.value),
                    },
                  })
                }
              />
            </div>
          )}

          <footer className="rk-popover-foot">
            <span>Tested on each source row.</span>
          </footer>
        </div>
      )}
    </Chip>
  );
}

/**
 * The bar's OWN value control, for a vocabulary the model states outright.
 *
 * Stays in this package rather than moving to `@clockwise/ui` with the chip
 * shell around it, because it is not presentation: what it offers, and what it
 * says when it can offer nothing, are read off the `Dimension` — and a
 * data-agnostic package must not learn what a dimension is. The dashboard has
 * no use for it either; every chip on that bar is served by the app's entity
 * picker.
 */
function OptionChip({
  containerRef,
  ruleId,
  filter,
  label,
  hint,
  values,
  options,
  op,
  type,
  operatorSwitch,
  others,
  onOp,
  openOnMount,
  chrome,
  onChange,
  onClose,
}: {
  ruleId: string;
  op: FilterOp;
  type: FilterValueType;
  /** Drawn above the search box. See {@link splitOperators}. */
  operatorSwitch: OperatorSwitch | null;
  /** The chip's own operator menu, when the column offers more than members. */
  others: readonly FilterOp[];
  onOp: (op: FilterOp) => void;
  /** The chip element, owned by {@link ChipSlot} — see why there. */
  containerRef: RefObject<HTMLDivElement | null>;
  filter: Dimension;
  /** The field's label, which the host may have overridden. */
  label: string;
  hint?: string;
  values: string[];
  // `readonly`, because a model's authored list is a module constant that
  // several chips share: handing one out as mutable is an invitation to sort
  // it in place and reorder every other picker over the same dimension.
  options: readonly DimValue[];
  openOnMount: boolean;
  onChange: (values: string[]) => void;
  onClose: () => void;
  chrome: ChipChrome;
}) {
  const [open, setOpen] = useState(openOnMount);
  const [query, setQuery] = useState('');
  // Read through a ref so `close` can stay identity-stable — `useDismiss`
  // re-binds its document listeners whenever it changes, and re-binding on
  // every keystroke in the search box would drop the press it is listening for.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const close = useCallback(() => {
    setOpen(false);
    onCloseRef.current();
  }, []);
  useDismiss(open, containerRef, close);

  const needle = query.trim().toLowerCase();
  const matches = needle
    ? options.filter((option) => (option.label ?? option.key ?? '').toLowerCase().includes(needle))
    : options;

  // Values are KEYS; the label is only what the reader sees.
  const toggle = (key: string) =>
    onChange(values.includes(key) ? values.filter((v) => v !== key) : [...values, key]);
  const labelOf = (key: string) => options.find((o) => o.key === key)?.label ?? key;

  /**
   * With no option list, the box takes the VALUE rather than searching for it.
   *
   * The alternative is a filter that can be added and never set, which is what
   * this bar did until the fixture that made it look otherwise was removed.
   * Typing works because a filter compares against the KEY: for a status, a
   * billable flag or a derived bucket the key is the word itself. For an
   * id-keyed dimension — Customer, Employee — it is a uuid, and the honest
   * answer is that this control cannot help; `renderControl` is the seam where
   * the app puts its own entity picker.
   */
  const freeText = options.length === 0;
  // Two ways to end up with no list, and they are not the same news. A
  // dimension declaring `'none'` has no enumerable members and never will —
  // typing IS the control. Anything else with no list is a control that has not
  // been supplied, and saying "no option list for this one YET" is what stops
  // the deliberate case from reading as an unfinished one.
  const byDesign = filterOptions(filter) === 'none';
  const addTyped = () => {
    const value = query.trim();
    if (!value || values.includes(value)) return;
    onChange([...values, value]);
    setQuery('');
  };

  // NOTHING when nothing is chosen, rather than the word "any". An empty chip
  // is a transient state now — it lives only while its panel is open, and the
  // bar takes it back off when that panel is dismissed unfilled — so the word
  // was a label for a state the reader is never left looking at, and on a bar
  // of four filters it read as four values that had been picked.
  const summary =
    values.length === 0
      ? undefined
      : values.length <= 2
        ? values.map(labelOf).join(', ')
        : `${values.length} selected`;

  return (
    <Chip ref={containerRef} ruleId={ruleId} active={values.length > 0} chrome={chrome}>
      <FilterChipTrigger
        name={`${label} ${chipOpLabel(op, type, values, chrome.pinned)}`.trim()}
        value={summary}
        expanded={open}
        hint={hint}
        title={hint ?? filter.description}
        onClick={() => (open ? close() : setOpen(true))}
      />
      {others.length > 1 && (
        <OperatorSegment op={op} operators={others} type={type} onChange={onOp} />
      )}

      {open && (
        <div className="rk-popover rk-popover-values" role="dialog" aria-label={label}>
          {operatorSwitch && (
            <FilterOperatorSwitch
              {...operatorSwitch}
              label={`${label} operator`}
              className="rk-op-switch"
            />
          )}
          <input
            className="rk-search"
            type={freeText ? 'text' : 'search'}
            // biome-ignore lint/a11y/noAutofocus: the popover opened on an explicit click
            autoFocus
            placeholder={
              freeText
                ? `Type a ${label.toLowerCase()} and press Enter…`
                : `Search ${label.toLowerCase()}…`
            }
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (!freeText || event.key !== 'Enter') return;
              event.preventDefault();
              addTyped();
            }}
          />
          <div className="rk-options">
            {freeText && values.length === 0 && (
              <p className="rk-options-empty">
                {byDesign
                  ? 'No fixed list for this one — type the value it is stored as.'
                  : 'No option list for this one yet — type the value it is stored as.'}
              </p>
            )}
            {!freeText && matches.length === 0 && (
              <p className="rk-options-empty">Nothing matches “{query}”.</p>
            )}
            {/* Chosen values are listed even with no option list, so what was
                typed can be seen and untyped. */}
            {(freeText ? values : matches.map((o) => o.key ?? '')).map((key) => (
              <label className="rk-check" key={key}>
                <input
                  type="checkbox"
                  checked={values.includes(key)}
                  onChange={() => toggle(key)}
                />
                <span>{labelOf(key)}</span>
              </label>
            ))}
          </div>
          <footer className="rk-popover-foot">
            <span>{filter.description ?? ''}</span>
            {values.length > 0 && (
              <button type="button" className="rk-link" onClick={() => onChange([])}>
                Clear
              </button>
            )}
          </footer>
        </div>
      )}
    </Chip>
  );
}
